import React from "react";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import ScrollArea from "../ui/shared/ScrollArea";
import { useAssistants } from "@/hooks/useAssistants";
import { usePipelines } from "@/hooks/usePipelines";
import { useState } from "react";
import { toast } from "sonner";
import CampaignTagSelector from "./CampaignTagSelector";
import DistributeEquallySelection from "./DistributeEquallySelection";
import { useTeam } from "@/hooks/useTeam";

type CampaignInsert = {
  name: string
  assistant_id: string | null
  trigger_type: string
  trigger_keywords: string[] | null
  tags: string[] | null
  pipeline_id: string | null
  pipeline_stage_id: string | null
  is_active: boolean | null
  distribution_member_ids: string[] | null
  last_distribution_index: number | null
}

interface AddNewCampaignProps {
  createCampaign: (data: CampaignInsert) => Promise<boolean>;
}

const AddNewCampaign: React.FC<AddNewCampaignProps> = ({ createCampaign }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { assistants } = useAssistants();
  const { pipelines, pipelineStages } = usePipelines();
  const { members } = useTeam();

  const [formData, setFormData] = useState({
    name: '',
    assistant_id: '',
    trigger_type: 'contains',
    trigger_keywords: [] as string[],
    tags: [] as string[],
    pipeline_id: '',
    pipeline_stage_id: '',
    is_active: true,
    distribution_member_ids: [] as string[],
  });

  const [keywordInput, setKeywordInput] = useState('');

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.trigger_keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        trigger_keywords: [...prev.trigger_keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      trigger_keywords: prev.trigger_keywords.filter(k => k !== keyword)
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      assistant_id: '',
      trigger_type: 'contains',
      trigger_keywords: [],
      tags: [],
      pipeline_id: '',
      pipeline_stage_id: '',
      is_active: true,
      distribution_member_ids: [],
    });
    setKeywordInput('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome da campanha é obrigatório');
      return;
    }

    if (!formData.trigger_type) {
      toast.error('Tipo de gatilho é obrigatório');
      return;
    }

    if (formData.pipeline_id && formData.pipeline_id !== 'none' && (!formData.pipeline_stage_id || formData.pipeline_stage_id === 'none')) {
      toast.error('Selecione uma etapa para o pipeline selecionado');
      return;
    }

    setLoading(true);

    try {
      const campaignData = {
        name: formData.name,
        assistant_id: formData.assistant_id === 'none' ? null : formData.assistant_id || null,
        trigger_type: formData.trigger_type,
        trigger_keywords: formData.trigger_keywords,
        tags: formData.tags,
        pipeline_id: formData.pipeline_id === 'none' ? null : formData.pipeline_id || null,
        pipeline_stage_id: formData.pipeline_stage_id === 'none' ? null : formData.pipeline_stage_id || null,
        is_active: formData.is_active,
        distribution_member_ids: formData.distribution_member_ids.length > 0 ? formData.distribution_member_ids : null,
        last_distribution_index: null,
      };

      const success = await createCampaign(campaignData);

      if (success) {
        toast.success('Campanha criada com sucesso!');
        resetForm();
        setOpen(false);
      } else {
        toast.error('Erro ao criar campanha. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error('Erro ao criar campanha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };

  // Filtrar stages baseado no pipeline selecionado
  const availableStages = pipelineStages.filter(stage => 
    stage.pipeline_id === formData.pipeline_id
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger className="border min-h-[44px] px-4 border-transparent bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl flex justify-center items-center gap-2 shadow-sm transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]">
        <p className="text-white font-medium text-[14px]">Nova campanha</p>
        <Plus size={18} className="text-white" />
      </DialogTrigger>
      <DialogContent className="flex !max-w-[600px] w-full p-0 rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] flex-col dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]">
        <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
          <div className="w-full flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Nova Campanha</DialogTitle>
            </DialogHeader>

            <form className="w-full flex flex-col gap-4">
              {/* Nome */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="name" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">Nome da Campanha *</Label>
                <Input
                  placeholder="Nome da campanha"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] text-[14px] min-h-[36px]"
                  type="text"
                  required
                />
              </div>

              {/* Assistente */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="assistant" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">Assistente</Label>
                <Select value={formData.assistant_id} onValueChange={(value) => handleFormChange('assistant_id', value)}>
                  <SelectTrigger className="h-11 !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] text-[14px] w-full">
                    <SelectValue placeholder="Selecione um assistente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">Nenhum assistente</SelectItem>
                      {assistants.filter(a => a.is_active).map((assistant) => (
                        <SelectItem key={assistant.id} value={assistant.id}>
                          {assistant.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              {/* Tipo de Gatilho */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="trigger_type" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">Tipo de Gatilho *</Label>
                <Select value={formData.trigger_type} onValueChange={(value) => handleFormChange('trigger_type', value)}>
                  <SelectTrigger className="h-11 !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] text-[14px] w-full">
                    <SelectValue placeholder="Selecione o tipo de gatilho" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="first_contact">Primeiro Contato</SelectItem>
                      <SelectItem value="contains">Contém</SelectItem>
                      <SelectItem value="starts_with">Inicia com</SelectItem>
                      <SelectItem value="any">Qualquer mensagem</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              {/* Palavras-chave */}
              {formData.trigger_type !== 'any' && formData.trigger_type !== 'first_contact' && (
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="keywords" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">Palavras-chave</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Digite uma palavra-chave"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                      className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] text-[14px] min-h-[36px]"
                      type="text"
                    />
                    <Button type="button" onClick={handleAddKeyword} className="min-w-[80px] rounded-xl bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors duration-200">
                      Adicionar
                    </Button>
                  </div>
                  {formData.trigger_keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.trigger_keywords.map((keyword, index) => (
                        <div key={index} className="flex items-center gap-2 rounded-full px-2.5 py-1 bg-[#DBEAFE] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]">
                          <span className="text-sm">{keyword}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveKeyword(keyword)}
                            className="text-[#64748B] hover:text-[#DC2626] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#F87171]"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Pipeline */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="pipeline" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">Pipeline</Label>
                <Select 
                  value={formData.pipeline_id} 
                  onValueChange={(value) => {
                    handleFormChange('pipeline_id', value);
                    handleFormChange('pipeline_stage_id', ''); // Reset stage when pipeline changes
                  }}
                >
                  <SelectTrigger className="h-11 !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] text-[14px] w-full">
                    <SelectValue placeholder="Selecione um pipeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">Nenhum pipeline</SelectItem>
                      {pipelines.map((pipeline) => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              {/* Etapa do Pipeline */}
              {formData.pipeline_id && formData.pipeline_id !== 'none' && (
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="pipeline_stage" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">Etapa *</Label>
                  <Select value={formData.pipeline_stage_id} onValueChange={(value) => handleFormChange('pipeline_stage_id', value)}>
                    <SelectTrigger className="h-11 !h-[40px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] text-[14px] w-full">
                      <SelectValue placeholder="Selecione uma etapa" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="none">Nenhuma etapa</SelectItem>
                        {availableStages.map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            {stage.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Tags */}
              <CampaignTagSelector
                selectedTags={formData.tags}
                onTagsChange={(tags) => handleFormChange('tags', tags)}
              />

              {/* Distribuição de Leads */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">Distribuição de Leads</Label>
                <p className="text-xs text-[#64748B] dark:text-[#71717A] -mt-1">
                  Selecione os membros que receberão leads em formato roleta
                </p>
                <DistributeEquallySelection
                  options={members.filter(m => m.is_active)}
                  selectedIds={formData.distribution_member_ids}
                  onSelectionChange={(ids) => handleFormChange('distribution_member_ids', ids)}
                  placeholder="Selecionar membros da equipe"
                />
              </div>

              <div className="w-full flex justify-end gap-3 mt-4">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="min-w-[120px] rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                >
                  Cancelar
                </Button>
                <Button 
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="min-w-[120px] rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewCampaign;